import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Grid, Skeleton, Stack, useMediaQuery, useTheme } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { useAtom } from 'jotai'
import { IssueType } from '@finance-lab/utils/types'
import { MinimumDistanceSlider } from './MinimumDistanceSlider'
import DropdownSelect from '../../components/DropdownSelect'
import DropdownTreeView, { DropdownTreeViewItems } from './DropdownTreeView'
import { IssueEntity } from '../../../types/IssueEntity'
import UserEventApi from '../../../api/UserEventApi'
import { getAdvisors, getSectors } from './utils/filters'
import ToggleIssueTypes from '../../components/ToggleIssueTypes'
import MarketplacesApi from '../../../api/MarketplacesApi'
import { Marketplace } from '../../../types/Marketplace'
import { issueFiltersAtom, IssueFilterType } from '../../../atoms/issueAtom'
import ToggleWatchLists from '../../components/ToggleWatchLists'
import { SelectDateRange } from '../../components/SelectDateRange'

type IssueFilters = typeof issueFiltersAtom

interface IssueFiltersProps {
    isLoading: boolean
    issues: IssueEntity[] | null
    offeredSizeMinValue?: number
}

const abbAlternatives = [
    { id: 'all', label: 'Alla riktade emissioner' },
    { id: 'only-abb', label: 'Endast ABB' },
    { id: 'exclude-abb', label: 'Exkludera ABB' },
]
const warrantsAlternatives = [
    { id: 'all', label: 'Alla emissioner' },
    { id: 'only-warrants', label: 'Endast emissioner med TO' },
    { id: 'exclude-warrants', label: 'Endast emissioner utan TO' },
]
const guarantorAlternatives = [
    { id: 'all', label: 'Alla emissioner' },
    { id: 'only-guarantors', label: 'Endast emissioner med garanter' },
    { id: 'no-guarantors', label: 'Endast emissioner utan garanter' },
]
const issueTermsAlternatives = [
    { id: 'all', label: 'Alla företrädesemissioner' },
    { id: 'only-issue-terms', label: 'Teckningskurs framgick initialt' },
    { id: 'no-issue-terms', label: 'Teckningskurs saknades initialt' },
]

const userEventApi = new UserEventApi()

const IssueFilters = ({ isLoading, issues, offeredSizeMinValue = 0 }: IssueFiltersProps) => {
    const [filters, setFilters] = useAtom(issueFiltersAtom)
    const { financialAdvisors, legalAdvisors } = useMemo(() => getAdvisors(issues || []), [issues])
    const sectors = useMemo(() => getSectors(issues || []), [issues])
    const [allMarketplaces, setAllMarketplaces] = useState<Marketplace[]>([])
    const [filteredMarketplaces, setFilteredMarketplaces] = useState<DropdownTreeViewItems>({})
    const [marketplacesWithIssues, setMarketplacesWithIssues] = useState<string[]>([])
    const [showMoreFilters, setShowMoreFilters] = useState(false)

    useEffect(() => {
        const fetchMarkets = async () => {
            const api = new MarketplacesApi()
            const markets = await api.allMarketplaces()

            setAllMarketplaces(markets)
        }

        void fetchMarkets()
    }, [])

    useEffect(() => {
        if (!issues?.length) return

        const marketplaces = new Set<string>()

        issues.forEach((issue) => {
            marketplaces.add(issue.marketplaceId)
        })

        setMarketplacesWithIssues([...marketplaces])
    }, [issues])

    useEffect(() => {
        const groupedMarketLists = allMarketplaces
            .filter((marketplace) => !!marketplace.listName && marketplacesWithIssues.includes(marketplace.id))
            .reduce((acc, val) => {
                const key = val.marketplaceName
                const arr = acc[key] || []

                return {
                    ...acc,
                    [key]: [
                        ...arr,
                        {
                            id: val.id,
                            label: val.listName,
                        },
                    ],
                }
            }, {})

        setFilteredMarketplaces(groupedMarketLists)
    }, [allMarketplaces, marketplacesWithIssues])

    const handleFilterChange = useCallback(
        async (filterType: keyof IssueFilterType, selectedItems: boolean | (string | Date)[]) => {
            setFilters((prevFilters) => {
                if (prevFilters[filterType].isSingleChoice && Array.isArray(selectedItems)) {
                    return {
                        ...prevFilters,
                        [filterType]: {
                            ...prevFilters[filterType],
                            value: selectedItems.filter(
                                (i) => !(prevFilters[filterType].value as Array<unknown>).includes(i),
                            ),
                        },
                    }
                }

                return {
                    ...prevFilters,
                    [filterType]: { ...prevFilters[filterType], value: selectedItems },
                }
            })
            await userEventApi.logEvent(UserEventApi.EVENT_TYPES.FILTER_ISSUES, {
                filterType,
                selectedItems,
            })
        },
        [setFilters],
    )

    const handleIssueTypeFilterChange = useCallback(
        (v: IssueType[]) => handleFilterChange('issueTypes', v),
        [handleFilterChange],
    )

    return (
        <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 3, sm: 0 }}>
                <Stack
                    sx={{
                        height: 116,
                        minWidth: 200,
                        pr: 3,
                        alignItems: 'center',
                    }}
                >
                    {isLoading && <Skeleton variant='rounded' width='100%' height='100%' />}
                    {!isLoading && (
                        <ToggleIssueTypes
                            disabledItems={filters.issueTypes.disabledItems}
                            onChange={(v) => void handleIssueTypeFilterChange(v)}
                            value={filters.issueTypes.value}
                            excludeTypes={['Warrant', 'Convertible']}
                        />
                    )}
                </Stack>
                <Grid
                    container
                    spacing={3}
                    sx={{
                        flexGrow: 1,
                        width: '100%',
                        maxWidth: '100%',
                    }}
                >
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <SelectDateRange
                                minDate='2020-01-01'
                                onChange={(values) => void handleFilterChange('dates', values)}
                                values={filters.dates.value}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownSelect
                                label='Finansiell rådgivare'
                                items={financialAdvisors}
                                multiple
                                selectedItems={filters.financialAdvisors.value}
                                onChange={(selectedItems) =>
                                    void handleFilterChange('financialAdvisors', selectedItems)
                                }
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownSelect
                                label='Legal rådgivare'
                                items={legalAdvisors}
                                multiple
                                selectedItems={filters.legalAdvisors.value}
                                onChange={(selectedItems) => void handleFilterChange('legalAdvisors', selectedItems)}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownTreeView
                                items={sectors}
                                label='Bransch'
                                pluralLabel='Branscher'
                                selectedItems={filters.sectors.value}
                                onChange={(selectedItems) => void handleFilterChange('sectors', selectedItems)}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <ToggleWatchLists
                                onChange={(v) => void handleFilterChange('onlyInWatchLists', v)}
                                value={filters.onlyInWatchLists.value}
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <MinimumDistanceSlider
                                label={'Pre-money'}
                                allSelectedMessage='Alla bolag'
                                value={filters.preMoney.value as [number, number]}
                                includeGreaterValues={filters.preMoney.includeGreaterValues as boolean}
                                valueLimits={[0, filters.preMoney.maxValue as number]}
                                onChange={(value, isIncludeGreaterValues) =>
                                    setFilters((prevFilters) => ({
                                        ...prevFilters,
                                        preMoney: {
                                            ...prevFilters.preMoney,
                                            value: value,
                                            hasMaxValueSet: isIncludeGreaterValues,
                                        },
                                    }))
                                }
                                unit='MSEK'
                                includeInputFields
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <MinimumDistanceSlider
                                label={'Erbjudandets storlek'}
                                allSelectedMessage='Alla emissioner'
                                value={[
                                    Math.max(filters.offeredSize.value[0], offeredSizeMinValue),
                                    filters.offeredSize.value[1],
                                ]}
                                includeGreaterValues={filters.offeredSize.includeGreaterValues as boolean}
                                valueLimits={[offeredSizeMinValue, filters.offeredSize.maxValue as number]}
                                onChange={(value, isIncludeGreaterValues) =>
                                    setFilters((prevFilters) => ({
                                        ...prevFilters,
                                        offeredSize: {
                                            ...prevFilters.offeredSize,
                                            value: value,
                                            hasMaxValueSet: isIncludeGreaterValues,
                                        },
                                    }))
                                }
                                unit='MSEK'
                                includeInputFields
                            />
                        )}
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                        {isLoading && <Skeleton variant='rounded' width='100%' height='52px' />}
                        {!isLoading && (
                            <DropdownTreeView
                                label='Listor'
                                pluralLabel='Listor'
                                items={filteredMarketplaces}
                                selectedItems={filters.marketplaces.value}
                                onChange={(selectedItems) => void handleFilterChange('marketplaces', selectedItems)}
                            />
                        )}
                    </Grid>
                    {showMoreFilters ? (
                        <>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    label='Unitemissioner'
                                    items={warrantsAlternatives}
                                    selectedItems={filters.warrants.value}
                                    onChange={(selectedItems) => void handleFilterChange('warrants', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    disabled={!filters.issueTypes.value.includes('Rights issue')}
                                    label='Emissionsvillkor'
                                    items={issueTermsAlternatives}
                                    selectedItems={filters.issueTerms.value}
                                    onChange={(selectedItems) => void handleFilterChange('issueTerms', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    label='Garantiåtagare'
                                    items={guarantorAlternatives}
                                    selectedItems={filters.guarantors.value}
                                    onChange={(selectedItems) => void handleFilterChange('guarantors', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <DropdownSelect
                                    disabled={!filters.issueTypes.value.includes('Directed issue')}
                                    label='Book building'
                                    items={abbAlternatives}
                                    selectedItems={filters.abb.value}
                                    onChange={(selectedItems) => void handleFilterChange('abb', selectedItems)}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <MinimumDistanceSlider
                                    label={'Börsvärde idag'}
                                    allSelectedMessage='Alla bolag'
                                    value={filters.marketSize.value as [number, number]}
                                    includeGreaterValues={filters.marketSize.includeGreaterValues as boolean}
                                    valueLimits={[0, filters.marketSize.maxValue as number]}
                                    onChange={(value, isIncludeGreaterValues) =>
                                        setFilters((prevFilters) => ({
                                            ...prevFilters,
                                            marketSize: {
                                                ...prevFilters.marketSize,
                                                value: value,
                                                hasMaxValueSet: isIncludeGreaterValues,
                                            },
                                        }))
                                    }
                                    unit='MSEK'
                                    includeInputFields
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <MinimumDistanceSlider
                                    label={'Börsvärde innan emission'}
                                    allSelectedMessage='Alla bolag'
                                    value={filters.marketSizePrePublication.value as [number, number]}
                                    includeGreaterValues={
                                        filters.marketSizePrePublication.includeGreaterValues as boolean
                                    }
                                    valueLimits={[0, filters.marketSizePrePublication.maxValue as number]}
                                    onChange={(value, isIncludeGreaterValues) =>
                                        setFilters((prevFilters) => ({
                                            ...prevFilters,
                                            marketSizePrePublication: {
                                                ...prevFilters.marketSizePrePublication,
                                                value: value,
                                                hasMaxValueSet: isIncludeGreaterValues,
                                            },
                                        }))
                                    }
                                    unit='MSEK'
                                    includeInputFields
                                />
                            </Grid>
                        </>
                    ) : null}
                </Grid>
            </Stack>
            <Box
                sx={{
                    textAlign: 'center',
                    py: 2,
                }}
            >
                <Button
                    disabled={isLoading}
                    onClick={() => setShowMoreFilters((prev) => !prev)}
                    endIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {isLoading && <Skeleton variant='rounded' width={150} />}
                    {!isLoading && (showMoreFilters ? 'Dölj avancerade filter' : 'Visa avancerade filter')}
                </Button>
            </Box>
        </>
    )
}

export default IssueFilters
